import { ChatMessage } from '../ngrx-store/app.state'

/** One message in a chat's list, with what to show around it. */
export interface ChatTimelineItem {
  message: ChatMessage
  /** "Today", "Yesterday", "Wed 24 Sep"... when this is the first message of its day, else null. */
  dayLabel: string | null
  /**
   * First of a run of messages from the same sender on the same day: only it shows the sender's
   * name and photo (in the national chat), and it's spaced off from the message before.
   */
  groupStart: boolean
  /** Last of its group: the corners where two bubbles of a group meet are tighter than the outer ones. */
  groupEnd: boolean
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/**
 * The day separator's text, in the viewer's time zone: "Today", "Yesterday", "Wed 24 Sep" this
 * year, else "24 Sep 2025". Spelled out, not Angular's DatePipe, so it reads the same in any locale.
 */
export function chatDayLabel(createdAt: string, now: Date = new Date()): string {
  const date = new Date(createdAt)
  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  if (daysAgo === 0) return 'Today'
  if (daysAgo === 1) return 'Yesterday'
  const dayMonth = `${date.getDate()} ${MONTHS[date.getMonth()]}`
  return date.getFullYear() === now.getFullYear()
    ? `${WEEKDAYS[date.getDay()]} ${dayMonth}`
    : `${dayMonth} ${date.getFullYear()}`
}

function sameDay(a: string, b: string): boolean {
  return startOfDay(new Date(a)) === startOfDay(new Date(b))
}

/** Same sender - an admin post counts as a different sender from the admin's own messages. */
function sameSender(a: ChatMessage, b: ChatMessage): boolean {
  return a.message_from === b.message_from && !!a.is_admin_post === !!b.is_admin_post
}

/** Whether `message` starts a group: the first of the list, of its day, or after another sender's. */
function startsGroup(previous: ChatMessage | undefined, message: ChatMessage): boolean {
  return !previous || !sameDay(previous.created_at, message.created_at) || !sameSender(previous, message)
}

/** Messages in order, each with its day separator (if it starts a day) and where it sits in its group. */
export function chatTimeline(messages: ChatMessage[] | null | undefined, now: Date = new Date()): ChatTimelineItem[] {
  const list = messages ?? []
  return list.map((message, i) => {
    const previous = list[i - 1]
    const next = list[i + 1]
    const newDay = !previous || !sameDay(previous.created_at, message.created_at)
    return {
      message,
      dayLabel: newDay ? chatDayLabel(message.created_at, now) : null,
      groupStart: startsGroup(previous, message),
      groupEnd: !next || startsGroup(message, next),
    }
  })
}
